import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { Form } from '@unform/web';
import { LinearProgress } from '@material-ui/core';
import { isEmpty, includes } from 'lodash';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Spinner } from '~/components';
import { Select, Input, CheckboxGroup, Autocomplete } from '~/components/Form';
import {
  GEOLOCALIZACAO,
  MODULO_20_GRAUS,
  LOCALIDADE_ABASTECIMENTO,
} from '~/constants/modules';
import { t, validCharacters, can } from '~/helpers';
import { storeComboio, updateComboio } from '~/store/modules/comboio/actions';

export default function CreateOrUpdate({
  reference,
  data,
  create,
  toggleLoading,
  toggleConfirmButton,
}) {
  const dispatch = useDispatch();

  const { client } = useSelector((state) => state.user.profile);
  const { modules } = useSelector((state) => state.user);
  const { loading, find, params } = useSelector((state) => state.comboio);

  const [yesOrNo, setYesOrNo] = useState(0);
  const [useInfoAbastPosto, setUseInfoAbastPosto] = useState(0);
  const [solicFrentSegundaLibPosto, setSolicFrentSegundaLibPosto] = useState(0);
  const [pendingRequests, setPendingRequests] = useState(0);
  const isAsyncLoading = pendingRequests > 0;
  const [showAsyncLoading, setShowAsyncLoading] = useState(false);
  const [hasLoadingError, setHasLoadingError] = useState(false);
  const loadingStartedAt = useRef(0);
  const loadingTimeoutRef = useRef(null);
  const loadingErrorMessage =
    's.geral.loadingerror';

  const initials = useMemo(
    () => (create ? data : { ...find, ...data }),
    [create, data, find]
  );

  const enableControla20 = yesOrNo === 1;
  const enableSolicFrentSegundaLibPosto = Number(useInfoAbastPosto) === 1;

  async function handleYesOrNo(value) {
    await reference.current.setFieldValue('controla20grausposto', value);
    await setYesOrNo(value);
  }

  async function handleUseInfoAbastPosto(value) {
    await reference.current.setFieldValue('utilinfoabastposto', value);
    await setUseInfoAbastPosto(value);
  }

  async function handleSolicFrentSegundaLibPosto(value) {
    await reference.current.setFieldValue('solicfrentsegundlibposto', value);
    await setSolicFrentSegundaLibPosto(value);
  }

  const handleAsyncLoadingChange = useCallback((isLoading) => {
    setPendingRequests((state) => {
      if (hasLoadingError) return 0;

      if (isLoading) return state + 1;

      return state > 0 ? state - 1 : 0;
    });
  }, [hasLoadingError]);

  const handleAsyncLoadingError = useCallback(() => {
    setHasLoadingError(true);
    setPendingRequests(0);
  }, []);

  async function handleSubmit(param) {
    try {
      if (hasLoadingError) {
        toast.error(t(loadingErrorMessage));
        return;
      }

      if (pendingRequests > 0) {
        toast.warning(
          t('s.geral.validacao.aguardecarregamento')
        );
        return;
      }

      const schema = Yup.object().shape({
        codigoposto: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(10, t('s.geral.validacao.tamanhomaximo').replace('%s', 10))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          ),
        nomeposto: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(50, t('s.geral.validacao.tamanhomaximo').replace('%s', 50))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          ),
        codigoauxiliarposto: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(20, t('s.geral.validacao.tamanhomaximo').replace('%s', 20))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          ),
        nomeabreviadoposto: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(10, t('s.geral.validacao.tamanhomaximo').replace('%s', 10))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          ),
        ajustehoraposto: Yup.number(t('s.geral.validacao.informeumnumero'))
          .required(t('s.geral.validacao.campoobrigatorio'))
          .min(-12, t('s.geral.validacao.valorminimo').replace('%s', -12))
          .max(12, t('s.geral.validacao.valormaximo').replace('%s', 12)),
        idveiculo: Yup.string()
          .nullable()
          .max(10, t('s.geral.validacao.tamanhomaximo').replace('%s', 10))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          ),
        possuittagcomboio: Yup.string().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
        leituracontinuattagcomboio: Yup.string().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
        statusposto: Yup.string().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
        cnpjposto: Yup.string().max(
          20,
          t('s.geral.validacao.tamanhomaximo').replace('%s', 20)
        ),
        depositoposto: Yup.string()
          .nullable()
          .max(10, t('s.geral.validacao.tamanhomaximo').replace('%s', 10)),
        centroposto: Yup.string()
          .nullable()
          .max(10, t('s.geral.validacao.tamanhomaximo').replace('%s', 10)),
        idpostoproprietario: Yup.string()
          .typeError(t('s.geral.validacao.campoobrigatorio'))
          .required(t('s.geral.validacao.campoobrigatorio')),
        tipofechamentoviagemcomboio: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .typeError(t('s.geral.validacao.campoobrigatorio')),
        controla20grausposto: can(MODULO_20_GRAUS)
          ? Yup.number()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .typeError(t('s.geral.validacao.campoobrigatorio'))
          : Yup.number(),
      });

      await schema.validate(param, {
        abortEarly: false,
      });

      const objSend = {
        ...initials,
        ...param,
        cercasvirtuais: isEmpty(param.cercasvirtuais)
          ? []
          : param.cercasvirtuais.split(',').map((value) => Number(value)),
        idcliente: client,
        tipoposto: 'C',
        acaogpscomboio: 0,
        tempogpscomboio: 0,
        latitudecomboio: 0.0,
        longitudecomboio: 0.0,
        instanteultimacoordenadacomboio: null,
        tipodescargaposto: 0,
        controla20grausposto: can(MODULO_20_GRAUS) ? yesOrNo : 0,
        operabastecimento20posto:
          can(MODULO_20_GRAUS) && yesOrNo === 1
            ? param.operabastecimento20posto
            : 0,
        idlocalidade: can(LOCALIDADE_ABASTECIMENTO)
          ? Number(param.idlocalidade) || null
          : null,
        utilinfoabastposto: Number(useInfoAbastPosto || 0),
        solicfrentsegundlibposto:
          Number(useInfoAbastPosto || 0) === 1
            ? Number(solicFrentSegundaLibPosto || 0)
            : 0,
      };

      if (create) {
        await dispatch(storeComboio(objSend, params));
      } else {
        await dispatch(updateComboio(objSend, params));
      }
    } catch (err) {
      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });

        reference.current.setErrors(validationErrors);
      }
    }
  }

  useEffect(() => {
    if (!loading && reference.current) {
      reference.current.setData(initials);
    }
  }, [loading, initials, reference]);

  useEffect(() => {
    toggleLoading(pendingRequests > 0);
  }, [pendingRequests, toggleLoading]);

  useEffect(() => {
    toggleConfirmButton(!hasLoadingError);
  }, [hasLoadingError, toggleConfirmButton]);

  useEffect(() => {
    if (isAsyncLoading) {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }

      loadingStartedAt.current = Date.now();
      setShowAsyncLoading(true);
      return;
    }

    const elapsed = Date.now() - loadingStartedAt.current;
    const delay = Math.max(0, 350 - elapsed);

    loadingTimeoutRef.current = setTimeout(() => {
      setShowAsyncLoading(false);
      loadingTimeoutRef.current = null;
    }, delay);
  }, [isAsyncLoading]);

  useEffect(
    () => () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }

      toggleLoading(false);
      toggleConfirmButton(true);
    },
    [toggleLoading, toggleConfirmButton]
  );

  useEffect(() => {
    if (!create) {
      setYesOrNo(find.controla20grausposto || 0);
      setUseInfoAbastPosto(find.utilinfoabastposto || 0);
      setSolicFrentSegundaLibPosto(find.solicfrentsegundlibposto || 0);
    }
  }, [find, create]);

  return (
    <>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : hasLoadingError ? (
        <div
          style={{
            border: '1px solid #f5c6cb',
            background: '#f8d7da',
            color: '#721c24',
            borderRadius: '4px',
            padding: '16px',
            lineHeight: 1.5,
          }}
        >
          {t(loadingErrorMessage)}
        </div>
      ) : (
        <div style={{ position: 'relative', minHeight: '14rem' }}>
          {showAsyncLoading && <LinearProgress />}
          <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
            <Input
              name="codigoposto"
              label={t('s.configuracao.comboio.idposto')}
              variant="outlined"
              fullWidth
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
            />

            <Input
              name="nomeposto"
              label={t('s.configuracao.posto.nomeposto')}
              variant="outlined"
              fullWidth
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
            />

            <Input
              name="codigoauxiliarposto"
              label={t('s.configuracao.posto.codigoauxiliarposto')}
              variant="outlined"
              fullWidth
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
            />

            <Input
              name="nomeabreviadoposto"
              label={t('s.configuracao.posto.nomeabreviadoposto')}
              variant="outlined"
              fullWidth
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
            />

            <Autocomplete
              name="idpostoproprietario"
              label="codigonomeposto"
              unique="idposto"
              title={t('s.cadastro.veiculo.postoproprietario')}
              url="postoscomboios"
              params={{
                wherefield: ['tipoposto'],
                valuewherefield: ['P'],
                typewherefield: ['EQUALS'],
              }}
              onLoadingChange={handleAsyncLoadingChange}
              onLoadingError={handleAsyncLoadingError}
            />

            <Input
              name="ajustehoraposto"
              label={t('s.configuracao.posto.ajustehoraposto')}
              variant="outlined"
              fullWidth
              type="number"
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
            />

            <Input
              name="idveiculo"
              label={t('s.configuracao.comboio.idveiculo')}
              variant="outlined"
              fullWidth
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
            />

            {can(LOCALIDADE_ABASTECIMENTO) && (
              <Autocomplete
                name="idlocalidade"
                label="nomelocalidade"
                unique="idlocalidade"
                title={t('s.configuracao.posto.localidade')}
                url="localidades"
                onSerialize={({ items }) =>
                  items.map((item) => ({
                    ...item,
                    nomelocalidade: `${item.nomelocalidade} [${item.idlocalidade}]`,
                  }))
                }
                params={{
                  wherefield: ['statuslocalidade'],
                  valuewherefield: ['L'],
                  typewherefield: ['EQUALS'],
                }}
                onLoadingChange={handleAsyncLoadingChange}
                onLoadingError={handleAsyncLoadingError}
              />
            )}

            <Select
              name="utilinfoabastposto"
              label={t('s.configuracao.posto.utilizainfoabastandamento')}
              options={[
                { value: 0, label: t('s.geral.opcoes.nao') },
                { value: 1, label: t('s.geral.opcoes.sim') },
              ]}
              onChange={({ target }) => handleUseInfoAbastPosto(target.value)}
              defaultValue={create ? 0 : initials.utilinfoabastposto || 0}
            />

            {enableSolicFrentSegundaLibPosto && (
              <Select
                name="solicfrentsegundlibposto"
                label={t('s.configuracao.posto.solicitafrentistasegundalibposto')}
                options={[
                  { value: 0, label: t('s.geral.opcoes.nao') },
                  { value: 1, label: t('s.geral.opcoes.sim') },
                ]}
                onChange={({ target }) =>
                  handleSolicFrentSegundaLibPosto(target.value)
                }
                defaultValue={
                  create ? 0 : initials.solicfrentsegundlibposto || 0
                }
              />
            )}

            <Select
              name="possuittagcomboio"
              label={t('s.configuracao.comboio.possuittagcomboio')}
              options={[
                { value: 'S', label: t('s.geral.opcoes.sim') },
                { value: 'N', label: t('s.geral.opcoes.nao') },
              ]}
            />

            <Select
              name="leituracontinuattagcomboio"
              label={t('s.configuracao.comboio.leituracontinuattagcomboio')}
              options={[
                { value: 'S', label: t('s.geral.opcoes.sim') },
                { value: 'N', label: t('s.geral.opcoes.nao') },
              ]}
            />

            <Input
              name="depositoposto"
              label={t('s.geral.camposistema.depositoposto')}
            />

            <Input
              name="centroposto"
              label={t('s.geral.camposistema.centroposto')}
            />

            <Input name="cnpjposto" label={t('s.configuracao.posto.cnpjposto')} />

            <Select
              name="tipofechamentoviagemcomboio"
              label={t('s.configuracao.comboio.tipofechamentoviagemcomboio')}
              options={[
                {
                  value: '2',
                  label: t('s.configuracao.terminal.telemetriaterminal'),
                },
                { value: '3', label: t('s.menu.configuracao.arqueacao') },
                { value: '4', label: t('s.menu.relatorio.complementoseta') },
              ]}
            />

            {can(GEOLOCALIZACAO) && (
              <CheckboxGroup
                name="cercasvirtuais"
                title={t('s.configuracao.comboio.cercasvirtuais')}
                label="descricaocercavirtual"
                unique="idcercavirtual"
                url="cercasvirtuais"
                onLoadingChange={handleAsyncLoadingChange}
                onLoadingError={handleAsyncLoadingError}
              />
            )}

            {/* {can(MODULO_20_GRAUS) && (
            <Select
              name="controla20grausposto"
              label={t('s.configuracao.posto.controla20graus')}
              options={[
                { value: 0, label: t('s.geral.opcoes.nao') },
                { value: 1, label: t('s.geral.opcoes.sim') },
              ]}
            />
          )} */}

            {can(MODULO_20_GRAUS) && (
              <Select
                name="controla20grausposto"
                label={t('s.configuracao.posto.controla20graus')}
                options={[
                  { value: 0, label: t('s.geral.opcoes.nao') },
                  { value: 1, label: t('s.geral.opcoes.sim') },
                ]}
                onChange={({ target }) => handleYesOrNo(target.value)}
                defaultValue={create ? 0 : initials.controla20grausposto}
              />
            )}

            {enableControla20 && (
              <>
                {can(MODULO_20_GRAUS) && (
                  <Select
                    name="operabastecimento20posto"
                    label={t('s.configuracao.posto.operabastecimento20')}
                    options={[
                      { value: 0, label: t('s.geral.opcoes.nao') },
                      { value: 1, label: t('s.geral.opcoes.sim') },
                    ]}
                  />
                )}
              </>
            )}

            <Select
              name="statusposto"
              variant="outlined"
              label={t('s.configuracao.posto.statusposto')}
              size="small"
              options={[
                { value: 'L', label: t('s.geral.opcoes.liberado') },
                { value: 'B', label: t('s.geral.opcoes.bloqueado') },
                { value: 'C', label: t('s.geral.opcoes.cancelado') },
              ]}
            />
          </Form>
          <Spinner loading={showAsyncLoading} />
        </div>
      )}
    </>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
  toggleLoading: PropTypes.func,
  toggleConfirmButton: PropTypes.func,
};

CreateOrUpdate.defaultProps = {
  data: {},
  toggleLoading: () => { },
  toggleConfirmButton: () => { },
};
